import {
	type CollectionModelDrawable,
	CollectionModelDrawableLine,
	CollectionModelDrawableMarker,
} from '@ikegps/core-shared';
import { DistanceInput } from '../../components/input/distance.input';
import { DistanceInputState } from '../../components/input/distance.input.state';
import { Input } from '../../components/input/input';
import { InputState } from '../../components/input/input.state';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { type ModalResponse, ModalService } from '../../components/modal/modal.service';
import { CSS as ModalCSS } from '../../components/modal/modal.style';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';

const CSS = {
	content: 'c-DrawableProperties',
	title: 'c-DrawableProperties__Title',
};

addStyles({
	[CSS.content]: {
		'min-height': DefaultTheme.space(20),
	},
	[CSS.title]: {
		'font-weight': 'bold',
		'padding-bottom': DefaultTheme.space(4),
	},
});

export class DrawablePropertiesComponent extends IMithrilStateComponent {
	state: InputState<string | number>;
	offsetState: DistanceInputState;
	isBase: boolean = false;

	constructor(drawable: CollectionModelDrawable) {
		super();

		if (CollectionModelDrawableMarker.isBase(drawable)) {
			// A locked base was derived from a paired ground shot, not placed against a stick, so there is no
			// offset to enter and the model refuses the write. Leaving `state` null makes `isValid` false, which
			// is the existing signal for "this drawable has no properties dialog".
			if (drawable.locked) {
				return;
			}

			this.offsetState = new DistanceInputState(T.DRAWABLE_BASE_OFFSET);
			this.offsetState.setValue(drawable.offset || 0);
			this.offsetState.isAutoFocused = true;
			this.isBase = true;
			this.state = this.offsetState;
		} else if (CollectionModelDrawableLine.isLine(drawable) || CollectionModelDrawableMarker.isMarker(drawable)) {
			this.state = new InputState(T.DRAWABLE_TITLE);
			this.state.setValue(drawable.label.text[0] || '');
			this.state.isAutoFocused = true;
			this.state.isLabelShown = false;
			this.state.name = 'marker-label';
		}
	}

	get isValid() {
		return this.state != null;
	}

	get nodes(): MithrilVNode {
		let content: MithrilVNode = null;
		if (this.isBase) {
			content = DistanceInput.view(this.offsetState);
		} else {
			content = Input.view(this.state);
		}

		return m('div', { className: CSS.content }, [
			m('div', { className: CSS.title }, Translate(this.state.label)),
			content,
		]);
	}

	viewActions() {
		const disabled = this.state.hasError();
		return m('div', { className: ModalCSS.actions }, [
			m(
				'button',
				{
					className: 'mdl-button ' + ModalCSS.action.cancel,
					type: 'button',
					onclick: () => ModalService.close(false),
				},
				Translate(T.CANCEL)
			),
			m(
				'button',
				{ className: 'mdl-button mdl-button--accent ' + ModalCSS.action.ok, disabled, type: 'submit' },
				Translate(T.OK)
			),
		]);
	}

	open(): Promise<ModalResponse> {
		return ModalService.open({
			title: T.SETTINGS_TITLE,
			onSubmit: () => ModalService.close(true),
			content: () => this.nodes,
			actions: () => this.viewActions(),
		});
	}
}
